import { LoaderFunction } from 'react-router-dom';

import { client } from '../../../../gql/client';
import { FETCH_TEAMS, FETCH_OPERATORS } from '../../../../gql/queries/users';
import { FETCH_EXTERNAL_COMPANIES } from '../../../../gql/queries/actions/externalCompanies';
import { FETCH_LABOR_HOURS_REPORT } from '../../../../gql/queries/reporting/laborHoursReport';
import { AuthContextType } from '../../../../hooks/useAuth';

import { tableIds } from '../../../../components/display/tables/types';
import { store } from '../../../../state/configureStore';
import { getCursor } from '../../../../state/selectors/ui/tables';
import { loadingData } from '../../../../state/actions/ui/loadingData';

import { defaultDateRange, cursorToFilters, toReportFilter } from './utils';
import { OperatorRow } from './types';

/**
 * Loader del informe Gestió Horària.
 *
 * COPIA del patrón canónico HistoricalAccidentsReport/loader.ts:
 *   1. Lee el cursor persistido en Redux (filtros) → construye el filter.
 *   2. Pre-carga filter options (teams + operators) cache-first.
 *   3. Lee el report de la cache de Apollo con `client.readQuery`
 *      (síncrono). Cache HIT → devuelve datos resueltos → la página los
 *      pinta INSTANTÁNEAMENTE sin spinner (igual que las otras tablas al
 *      volver a entrar). Cache MISS → `loadingData(true)` + `client.query`
 *      network-only + `loadingData(false)`.
 *   4. `({ data: combinedData })` con el valor ya resuelto.
 *
 * El spinner (ContainerLoader + Loader) se controla con el flag Redux
 * `loadingData(tableIds.REPORTING_TIME_MANAGEMENT)` — solo true en cache
 * miss o en refetch (onReFetchData de la página). Cache hit = sin spinner.
 */
export const TimeManagementLoader: (
  userData: AuthContextType | null,
) => LoaderFunction = (userData) => async () => {
  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  const initial = defaultDateRange();
  try {
    if (!userData?.user) return {};

    const dispatch = store.dispatch;
    const cursor = getCursor(tableIds.REPORTING_TIME_MANAGEMENT)(
      store.getState(),
    );
    const filters = cursorToFilters(cursor, initial);
    const reportFilter = toReportFilter(filters);

    // FIX (loader-loading-flag-leak): limpia flag fugado de un run anterior.
    dispatch(loadingData(tableIds.REPORTING_TIME_MANAGEMENT, false));

    // Filter options (teams + operators + empresas externas) cache-first.
    const [teamsResp, operatorsResp, externalResp] = await Promise.all([
      client.query({ query: FETCH_TEAMS, fetchPolicy: 'cache-first' }),
      client.query({ query: FETCH_OPERATORS, fetchPolicy: 'cache-first' }),
      client.query({
        query: FETCH_EXTERNAL_COMPANIES,
        fetchPolicy: 'cache-first',
      }),
    ]);
    const filterOptions = {
      teams: teamsResp.data?.teams ?? [],
      operators: operatorsResp.data?.operators ?? [],
      externalCompanies: externalResp.data?.externalCompanies ?? [],
    };

    // Report — leer de cache (síncrono). Apollo particiona por `filter`.
    let cached: { laborHoursReport?: { rows: OperatorRow[] } } | null = null;
    try {
      cached = client.readQuery({
        query: FETCH_LABOR_HOURS_REPORT,
        variables: { filter: reportFilter },
      });
    } catch {
      cached = null;
    }
    const isCacheEmpty =
      !cached?.laborHoursReport || JSON.stringify(cached) === '{}';

    if (isCacheEmpty) {
      // Cache miss → network-only con el flag de loading activo.
      dispatch(loadingData(tableIds.REPORTING_TIME_MANAGEMENT, true));
      try {
        const { data } = await client.query({
          query: FETCH_LABOR_HOURS_REPORT,
          variables: { filter: reportFilter },
          fetchPolicy: 'network-only',
        });
        return {
            filterOptions,
            report: data?.laborHoursReport ?? { rows: [] },
            filters,
          };
      } finally {
        dispatch(loadingData(tableIds.REPORTING_TIME_MANAGEMENT, false));
      }
    }

    // Cache hit → datos resueltos, sin spinner.
    return {
        filterOptions,
        report: cached?.laborHoursReport ?? { rows: [] },
        filters,
      };
  } catch (err) {
    // Offline fallback canónico (HistoricalAccidentsReport/loader.ts:231).
    console.log('Loader offline fallback:', err);
    store.dispatch(loadingData(tableIds.REPORTING_TIME_MANAGEMENT, false));
    return {
        filterOptions: { teams: [], operators: [], externalCompanies: [] },
        report: { rows: [] },
        filters: cursorToFilters(null, initial),
      };
  }
};

  return { data: load() };
};
